/**
 * Model Registry API Client
 *
 * Fetches AI model configurations from api-ai service.
 */

import type { AINodeType, NodeModelConfig, AiModelPurpose } from '@genieartinc/schemas';
import { aiGatewayCall, buildQueryString } from './api-helpers';

export interface ModelFamily {
  modelFamilySlug: string;
  modelFamily: string;
  featuredScore: number;
}

export interface ModelRegistryAPI {
  getFamiliesByNodeType(
    nodeType: AINodeType,
    purpose?: AiModelPurpose,
    subPurpose?: string
  ): Promise<ModelFamily[]>;
  getModelsByNodeType(
    nodeType: AINodeType,
    purpose?: AiModelPurpose,
    subPurpose?: string
  ): Promise<NodeModelConfig[]>;
  getModelsByFamily(
    nodeType: AINodeType,
    modelFamilySlug: string,
    purpose?: AiModelPurpose,
    subPurpose?: string
  ): Promise<NodeModelConfig[]>;
}

interface GatewayFamiliesPayload {
  data?: { families?: ModelFamily[] };
  families?: ModelFamily[];
}

interface GatewayModelsPayload {
  data?: { models?: NodeModelConfig[] };
  models?: NodeModelConfig[];
}

/**
 * Model Registry API client
 */
export const modelRegistryAPI: ModelRegistryAPI = {
  /**
   * Get model family list for a specific node type
   */
  async getFamiliesByNodeType(
    nodeType: AINodeType,
    purpose?: AiModelPurpose,
    subPurpose?: string
  ): Promise<ModelFamily[]> {
    const qs = buildQueryString({ purpose, subPurpose });
    const result = await aiGatewayCall(`/models/families/${nodeType}${qs}`) as GatewayFamiliesPayload;

    const payload = result.data || result;
    return payload.families || [];
  },

  /**
   * Get all enabled models for a specific node type
   */
  async getModelsByNodeType(
    nodeType: AINodeType,
    purpose?: AiModelPurpose,
    subPurpose?: string
  ): Promise<NodeModelConfig[]> {
    const qs = buildQueryString({ purpose, subPurpose });
    const result = await aiGatewayCall(`/models/${nodeType}${qs}`) as GatewayModelsPayload;

    const payload = result.data || result;
    return payload.models || [];
  },

  /**
   * Get models for a specific model family
   */
  async getModelsByFamily(
    nodeType: AINodeType,
    modelFamilySlug: string,
    purpose?: AiModelPurpose,
    subPurpose?: string
  ): Promise<NodeModelConfig[]> {
    const qs = buildQueryString({ purpose, subPurpose });
    const result = await aiGatewayCall(`/models/${nodeType}/${modelFamilySlug}${qs}`) as GatewayModelsPayload;

    const payload = result.data || result;
    return payload.models || [];
  },
};
